import * as THREE from 'three';
import { Sizes } from './Sizes';
import { Scene } from './Scene';
import { SmoothScroll } from './SmoothScroll';
import EventEmitter from '../Utils/EventEmitter';
import Time from './Time';
import { Config } from '../Config';
import { Renderer } from './Renderer';

/**
 * Camera class
 * Manages the THREE.js camera with smooth and immediate movement options
 */
export class Camera extends EventEmitter {
  // Static instance property
  static instance = null;

  /**
   * Get the singleton instance of the class
   * @returns {Camera} The singleton instance
   */
  static getInstance() {
    if (!Camera.instance) {
      Camera.instance = new Camera();
    }
    return Camera.instance;
  }

  /**
   * Constructor
   */
  constructor() {
    // Ensure this is a singleton
    if (Camera.instance) {
      return Camera.instance;
    }

    super();

    this.sizes = Sizes.getInstance();
    this.scene = Scene.getInstance();
    this.time = Time.getInstance();
    this.scroll = SmoothScroll.getInstance();
    this.bindAll();

    // Create camera
    this.createPerspectiveCamera();

    // Set up event listeners
    this.setupListeners();
    // Set instance
    Camera.instance = this;
  }

  bindAll() {
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  /**
   * Create perspective camera
   */
  createPerspectiveCamera() {
  
    this.instance = new THREE.PerspectiveCamera(
      Config.camera.fov,
      this.sizes.width / this.sizes.height,
      Config.camera.near,
      Config.camera.far
    );

    this.instance.position.set(
      Config.camera.initialPosition.x,
      Config.camera.initialPosition.y,
      Config.camera.initialPosition.z
    );

    this.maskCamera = this.instance.clone();
    this.maskCamera.name = 'maskCamera';


    this.scene.add(this.instance);
    this.scene.add(this.maskCamera);

    this.cameraTarget = this.instance.position.clone();
  }

  /**
   * Update camera position
   */
  update() {
    this.instance.position.y = this.cameraTarget.y;
    this.maskCamera.position.y = this.cameraTarget.y;
  }

  /**
   * Calculate camera viewport
   * @returns {Object} viewportTop and viewportBottom
   */
  calculateCameraViewport() {
    const cameraPosition = this.instance.position.y;
    const viewportTop = cameraPosition + (this.sizes.frustumSize.height / 2);
    const viewportBottom = cameraPosition - (this.sizes.frustumSize.height / 2);

    return {
      viewportTop,
      viewportBottom
    }
  }

  /**
   * Update camera position based on scroll
   * @param {Number} scrollY - Current scroll position
   */
  calculateCameraPosition() {
    const scrollProgress = this.scroll.getProgress()
    this.cameraTarget.y = -scrollProgress * this.sizes.worldScrollableHeight;
  }

  updateCamera() {
    this.calculateCameraPosition();
    this.update();
  }

  onScroll(scrollData) {
    if (!scrollData) {
      return;
    }

    this.updateCamera();
    this.trigger('scroll', [scrollData]);
  }

  onResize() {
    this.instance.aspect = this.sizes.aspectRatio;
    this.instance.updateProjectionMatrix();

    this.maskCamera.aspect = this.sizes.aspectRatio;
    this.maskCamera.updateProjectionMatrix();

    this.trigger('updated');
  }

  /**
   * Set up event listeners
   */
  setupListeners() {
    this.sizes.on('resize', this.onResize);
    this.scroll.on('scroll', this.onScroll);
  }

  /**
   * Get camera position
   * @returns {THREE.Vector3} Camera position
   */
  getPosition() {
    return this.instance.position.clone();
  }
}
